'use client';

import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { AiClientError, askDoddledaysAi, currentTripSlug, tripDaysFromPlannerPayload, type AiChatMessage, type AiLocationContext } from './aiClient';
import { createApiClient } from './apiClient';
import { useStoredAuthToken } from './authSession';
import { useI18n } from './i18n';

const LAST_PROJECT_KEY = 'webapp.lastProjectSlug';
const ACTIVE_SUBSCRIPTION_STATUSES = new Set(['active', 'trialing', 'past_due']);

type EntitlementState = 'idle' | 'checking' | 'plus' | 'free' | 'error';
type DisplayMessage = AiChatMessage & { id: string };

function SparkIcon({ size = 20 }: { size?: number }) {
  return (
    <svg aria-hidden="true" fill="none" height={size} viewBox="0 0 24 24" width={size}>
      <path d="M12 2.5c.7 4.2 2.8 6.3 7 7-4.2.7-6.3 2.8-7 7-.7-4.2-2.8-6.3-7-7 4.2-.7 6.3-2.8 7-7Z" fill="currentColor" />
      <path d="M18.5 15.5c.35 2.1 1.4 3.15 3.5 3.5-2.1.35-3.15 1.4-3.5 3.5-.35-2.1-1.4-3.15-3.5-3.5 2.1-.35 3.15-1.4 3.5-3.5ZM5 14c.25 1.5 1 2.25 2.5 2.5C6 16.75 5.25 17.5 5 19c-.25-1.5-1-2.25-2.5-2.5C4 16.25 4.75 15.5 5 14Z" fill="currentColor" opacity=".72" />
    </svg>
  );
}

function currentLocation(): Promise<AiLocationContext> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('geolocation unavailable'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: Number.isFinite(position.coords.accuracy) ? position.coords.accuracy : null,
        observed_at: new Date(position.timestamp || Date.now()).toISOString(),
      }),
      reject,
      { enableHighAccuracy: true, maximumAge: 0, timeout: 10_000 },
    );
  });
}

function messageId(role: AiChatMessage['role']): string {
  return `${role}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function GlobalAiAssistant() {
  const token = useStoredAuthToken();
  const location = useLocation();
  const { locale, t } = useI18n();
  const api = useMemo(() => createApiClient(), []);
  const [open, setOpen] = useState(false);
  const [entitlement, setEntitlement] = useState<EntitlementState>('idle');
  const [entitlementCheck, setEntitlementCheck] = useState(0);
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [quotaRemaining, setQuotaRemaining] = useState<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const storedSlug = typeof window === 'undefined' ? null : window.localStorage.getItem(LAST_PROJECT_KEY);
  const tripSlug = currentTripSlug(location.pathname, location.search, storedSlug);

  useEffect(() => {
    setMessages([]);
    setInput('');
    setError('');
    setQuotaRemaining(null);
    setEntitlement('idle');
    setOpen(false);
  }, [tripSlug]);

  useEffect(() => {
    if (!open || !token) return;
    let cancelled = false;
    setEntitlement('checking');
    void api.getSubscription(token)
      .then((subscription) => {
        if (cancelled) return;
        const plus = subscription.plan === 'plus' && ACTIVE_SUBSCRIPTION_STATUSES.has(subscription.status);
        setEntitlement(plus ? 'plus' : 'free');
      })
      .catch(() => { if (!cancelled) setEntitlement('error'); });
    return () => { cancelled = true; };
  }, [api, entitlementCheck, open, token]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    window.setTimeout(() => textareaRef.current?.focus(), 50);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, entitlement]);

  useEffect(() => {
    if (!open) return;
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, open, sending]);

  if (!token || !tripSlug) return null;

  async function send(message = input) {
    const clean = message.trim();
    if (!clean || sending || entitlement !== 'plus') return;
    const prior = messages.map(({ role, content }) => ({ role, content }));
    setError('');
    setSending(true);

    let freshLocation: AiLocationContext;
    let plannerPayload: unknown = null;
    try {
      [freshLocation, plannerPayload] = await Promise.all([
        currentLocation(),
        api.getProjectPlannerState(token!, tripSlug!)
          .then((state) => state.payload)
          .catch(() => null),
      ]);
    } catch {
      setError(t('assistant.locationUnavailable'));
      setSending(false);
      return;
    }

    setMessages((current) => [...current, { id: messageId('user'), role: 'user', content: clean }]);
    setInput('');
    try {
      const response = await askDoddledaysAi({
        token: token!,
        tripSlug: tripSlug!,
        message: clean,
        history: prior,
        location: freshLocation,
        locale,
        pathname: location.pathname,
        tripDays: tripDaysFromPlannerPayload(plannerPayload),
      });
      setMessages((current) => [...current, { id: messageId('assistant'), role: 'assistant', content: response.text }]);
      setQuotaRemaining(response.quota?.remaining ?? null);
    } catch (reason) {
      if (reason instanceof AiClientError && reason.status === 403) {
        setEntitlement('free');
        setError(t('assistant.plusRequired'));
      } else if (reason instanceof AiClientError && reason.status === 429) {
        setError(t('assistant.dailyLimit'));
        setQuotaRemaining(0);
      } else if (reason instanceof AiClientError && reason.code === 'AI_OFFLINE') {
        setError(t('assistant.offline'));
      } else if (reason instanceof AiClientError) {
        const errorKey = reason.stage === 'mcp'
          ? 'assistant.errorMcp'
          : reason.stage === 'model' || reason.stage === 'model_auth'
            ? 'assistant.errorModel'
            : reason.stage === 'quota'
              ? 'assistant.errorQuota'
              : reason.stage === 'auth'
                ? 'assistant.errorSession'
                : reason.stage === 'agent'
                  ? 'assistant.errorAgent'
                  : reason.stage === 'network'
                    ? 'assistant.errorNetwork'
                    : 'assistant.errorService';
        const diagnostic = [reason.code, reason.requestId ? `ref ${reason.requestId.slice(0, 8)}` : ''].filter(Boolean).join(' · ');
        setError(`${t(errorKey)}${diagnostic ? ` [${diagnostic}]` : ''}`);
      } else {
        setError(t('assistant.requestFailed'));
      }
    } finally {
      setSending(false);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    void send();
  }

  const suggestions = [t('assistant.promptNext'), t('assistant.promptWeather'), t('assistant.promptNearby')];

  return (
    <>
      <button
        aria-expanded={open}
        aria-label={t('assistant.open')}
        className="dd-ai-launcher"
        onClick={() => setOpen(true)}
        title={t('assistant.open')}
        type="button"
      >
        <SparkIcon size={21} />
        <span>{t('assistant.shortLabel')}</span>
      </button>

      {open ? (
        <div className="dd-ai-layer">
          <button aria-label={t('assistant.close')} className="dd-ai-backdrop" onClick={() => setOpen(false)} type="button" />
          <section aria-label={t('assistant.title')} aria-modal="true" className="dd-ai-panel" role="dialog">
            <header className="dd-ai-header">
              <div className="dd-ai-heading">
                <span className="dd-ai-mark"><SparkIcon size={19} /></span>
                <div>
                  <strong>{t('assistant.title')}</strong>
                  <span>{t('assistant.beta')}</span>
                </div>
              </div>
              <div className="dd-ai-header-actions">
                {messages.length ? (
                  <button className="dd-ai-text-action" onClick={() => { setMessages([]); setError(''); }} type="button">{t('assistant.clear')}</button>
                ) : null}
                <button aria-label={t('assistant.close')} className="dd-ai-close" onClick={() => setOpen(false)} type="button">×</button>
              </div>
            </header>

            {entitlement === 'checking' || entitlement === 'idle' ? (
              <div className="dd-ai-state" aria-live="polite">
                <span className="dd-ai-thinking-dot" />
                <p>{t('assistant.plusChecking')}</p>
              </div>
            ) : entitlement === 'free' ? (
              <div className="dd-ai-upsell">
                <span className="dd-ai-upsell-icon"><SparkIcon size={26} /></span>
                <h2>{t('assistant.plusTitle')}</h2>
                <p>{t('assistant.plusText')}</p>
                <Link className="dd-ai-plus-link" to={`/plus?source=ai&trip=${encodeURIComponent(tripSlug)}`} onClick={() => setOpen(false)}>{t('assistant.viewPlus')}</Link>
              </div>
            ) : entitlement === 'error' ? (
              <div className="dd-ai-state" aria-live="polite">
                <p>{t('assistant.loadFailed')}</p>
                <button className="dd-ai-text-action" onClick={() => setEntitlementCheck((value) => value + 1)} type="button">{t('assistant.retry')}</button>
              </div>
            ) : (
              <>
                <div className="dd-ai-messages" ref={scrollRef}>
                  {messages.length === 0 ? (
                    <div className="dd-ai-welcome">
                      <h2>{t('assistant.welcomeTitle')}</h2>
                      <p>{t('assistant.intro')}</p>
                      <div className="dd-ai-suggestions">
                        {suggestions.map((suggestion) => (
                          <button key={suggestion} onClick={() => void send(suggestion)} type="button">{suggestion}</button>
                        ))}
                      </div>
                    </div>
                  ) : messages.map((message) => (
                    <div className={`dd-ai-message is-${message.role}`} key={message.id}>
                      <span>{message.content}</span>
                    </div>
                  ))}
                  {sending ? <div className="dd-ai-message is-assistant is-thinking"><span className="dd-ai-thinking-dot" /><span>{t('assistant.thinking')}</span></div> : null}
                </div>

                <div className="dd-ai-compose-wrap">
                  {error ? <p aria-live="polite" className="dd-ai-error">{error}</p> : null}
                  <div className="dd-ai-context-row">
                    <span className="dd-ai-location is-active">
                      <span aria-hidden="true">⌖</span>
                      {t('assistant.locationAutomatic')}
                    </span>
                    {quotaRemaining !== null ? <span className="dd-ai-quota">{t('assistant.remaining', { count: quotaRemaining })}</span> : null}
                  </div>
                  <form className="dd-ai-composer" onSubmit={submit}>
                    <textarea
                      aria-label={t('assistant.placeholder')}
                      disabled={sending}
                      maxLength={2000}
                      onChange={(event) => setInput(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && !event.shiftKey) {
                          event.preventDefault();
                          if (input.trim()) void send();
                        }
                      }}
                      placeholder={t('assistant.placeholder')}
                      ref={textareaRef}
                      rows={2}
                      value={input}
                    />
                    <button aria-label={t('assistant.send')} disabled={sending || !input.trim()} type="submit">↑</button>
                  </form>
                  <p className="dd-ai-disclaimer">{t('assistant.disclaimer')}</p>
                </div>
              </>
            )}
          </section>
        </div>
      ) : null}
    </>
  );
}
